import { useMemo, useState } from 'react';

import { shrinkagePoints } from '../domain/chartdata.js';
import { fmtCv, fmtInt, fmtPct } from '../domain/format.js';
import { visibleDomainsForCell } from '../domain/gating.js';
import {
  cellStats,
  interpretationParagraphs,
  modelCv,
} from '../domain/interpretation.js';
import type { DomainResult } from '@sae/engine';
import { useOpenLicenseDialog, useProjectState, useStore } from './hooks.js';
import { ExportButtons } from './ExportButtons.js';
import { useActiveAccent } from './Appearance.js';

type SortKey = 'area' | 'n' | 'direct' | 'se' | 'model' | 'cv' | 'gamma';

function sortValue(d: DomainResult, key: SortKey): number | string {
  switch (key) {
    case 'area':
      return d.domain.areaId;
    case 'n':
      return d.reliability.n;
    case 'direct':
      return d.direct?.estimate ?? -Infinity;
    case 'se':
      return d.direct?.standardError ?? -Infinity;
    case 'model':
      return d.model?.estimate ?? -Infinity;
    case 'cv':
      return modelCv(d) ?? Infinity;
    case 'gamma':
      return d.reliability.gamma ?? -Infinity;
  }
}

function flagText(d: DomainResult): string {
  if (d.status === 'suppressed') return 'Suppressed: Do Not Publish';
  if (d.status === 'flagged') return 'Flagged: Unstable';
  return '';
}

/**
 * The direct-vs-model chart (mockup Step 3), as SVG — a forest plot
 * in the 2026-10-18 redesign: per area, the model interval as an
 * orange bar with end caps, the direct estimate as a gray dot, the
 * model estimate as an orange dot, and an arrow between them showing
 * the shrinkage pull. A right-hand strip reads the model CV as a
 * banded dot (teal to 20%, amber to 30%, red above) beside its value,
 * and flagged / suppressed rows are badged at the right edge and
 * dimmed. All geometry comes from shrinkagePoints; nothing is
 * computed here that the table does not also show.
 */
function ShrinkageChart({ domains }: { domains: readonly DomainResult[] }) {
  const points = useMemo(() => shrinkagePoints(domains), [domains]);
  const width = 860;
  const rowH = 24;
  const labelW = 150;
  const statusW = 84;
  const cvW = 62;
  const topPad = 20;
  const bottomPad = 24;
  const height = Math.max(
    60,
    topPad + points.length * rowH + bottomPad,
  );
  const plotX1 = width - statusW - cvW;
  const cvDotX = plotX1 + 16;
  const cvTextX = cvDotX + 9;
  const values = points.flatMap((p) =>
    [p.direct, p.model, p.lower, p.upper].filter(
      (v): v is number => v !== null,
    ),
  );
  const min = values.length > 0 ? Math.min(...values) : 0;
  const max = values.length > 0 ? Math.max(...values) : 1;
  const pad = (max - min) * 0.08 || 0.02;
  const x0 = min - pad;
  const x1 = max + pad;
  const x = (v: number) =>
    labelW + ((v - x0) / (x1 - x0)) * (plotX1 - labelW);
  const ticks = [0, 1, 2, 3, 4].map((t) => x0 + ((x1 - x0) * t) / 4);
  const cvClass = (cv: number) =>
    cv <= 0.2 ? 'chart-cv-ok' : cv <= 0.3 ? 'chart-cv-warn' : 'chart-cv-bad';

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="shrinkage-chart"
      role="img"
      aria-label="Direct estimates beside model estimates, with model intervals"
    >
      <defs>
        <marker
          id="pull-arrow"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6.5"
          markerHeight="6.5"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" className="chart-arrow-head" />
        </marker>
      </defs>
      {ticks.map((v, i) => (
        <g key={i}>
          <line
            x1={x(v)}
            x2={x(v)}
            y1={topPad - 6}
            y2={height - bottomPad + 2}
            className="chart-grid"
          />
          <text
            x={x(v)}
            y={height - 8}
            textAnchor="middle"
            className="chart-tick"
          >
            {fmtPct(v)}
          </text>
        </g>
      ))}
      <text x={cvDotX - 4} y={12} className="chart-cv-head">
        CV
      </text>
      {points.map((p, i) => {
        const cy = topPad + i * rowH + rowH / 2;
        const suppressed = p.status === 'suppressed';
        const flagged = p.status === 'flagged';
        const barClass = suppressed ? 'chart-bar is-suppressed' : 'chart-bar';
        const title =
          `${p.areaId}: direct ${p.direct !== null ? fmtPct(p.direct) : '—'}, ` +
          `model ${p.model !== null ? fmtPct(p.model) : '—'}` +
          (p.cv !== null ? `, model CV ${fmtCv(p.cv)}` : '') +
          (flagged
            ? '. Flagged: Unstable'
            : suppressed
              ? '. Suppressed: Do Not Publish'
              : '');
        return (
          <g key={p.areaId}>
            <title>{title}</title>
            {i % 2 === 0 && (
              <rect
                x={0}
                y={topPad + i * rowH}
                width={width}
                height={rowH}
                className="chart-alt-row"
                opacity={0.03}
              />
            )}
            <text
              x={8}
              y={cy + 4}
              className={`chart-area${flagged ? ' is-flagged' : ''}${suppressed ? ' is-suppressed' : ''}`}
            >
              {p.areaId}
            </text>
            {p.lower !== null && p.upper !== null && (
              <>
                <line
                  x1={x(p.lower)}
                  x2={x(p.upper)}
                  y1={cy}
                  y2={cy}
                  className={barClass}
                  strokeWidth={5}
                  strokeLinecap="round"
                  opacity={suppressed ? 0.3 : 0.38}
                  strokeDasharray={suppressed ? '2 3' : undefined}
                />
                <line
                  x1={x(p.lower)}
                  x2={x(p.lower)}
                  y1={cy - 4}
                  y2={cy + 4}
                  className={barClass}
                  strokeWidth={1.6}
                  opacity={0.85}
                />
                <line
                  x1={x(p.upper)}
                  x2={x(p.upper)}
                  y1={cy - 4}
                  y2={cy + 4}
                  className={barClass}
                  strokeWidth={1.6}
                  opacity={0.85}
                />
              </>
            )}
            {p.direct !== null && p.model !== null &&
              Math.abs(x(p.direct) - x(p.model)) >= 6 && (
                <line
                  x1={x(p.direct)}
                  x2={x(p.model)}
                  y1={cy}
                  y2={cy}
                  className="chart-pull"
                  strokeWidth={1.6}
                  opacity={0.9}
                  markerEnd="url(#pull-arrow)"
                />
              )}
            {p.direct !== null && (
              <circle cx={x(p.direct)} cy={cy} r={4} className="chart-direct" />
            )}
            {p.model !== null &&
              (suppressed ? (
                <circle
                  cx={x(p.model)}
                  cy={cy}
                  r={4.5}
                  className="chart-model is-suppressed"
                  strokeWidth={1.6}
                />
              ) : (
                <circle
                  cx={x(p.model)}
                  cy={cy}
                  r={4.5}
                  className="chart-model"
                  strokeWidth={1.2}
                />
              ))}
            {p.cv !== null && (
              <>
                <circle cx={cvDotX} cy={cy} r={4} className={cvClass(p.cv)} />
                <text x={cvTextX} y={cy + 3.5} className="chart-cv">
                  {fmtCv(p.cv)}
                </text>
              </>
            )}
            {(flagged || suppressed) && (
              <text
                x={width - 8}
                y={cy + 3}
                textAnchor="end"
                className={`chart-status${flagged ? ' is-flagged' : ''}${suppressed ? ' is-suppressed' : ''}`}
              >
                {suppressed ? 'SUPPRESSED' : 'FLAGGED'}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function Step3Results() {
  const store = useStore();
  const state = useProjectState();
  const openLicenseDialog = useOpenLicenseDialog();
  /* The chart caption names the active accent's color (owner
     direction, 2026-10-18); under the default it reads "orange",
     the original wording verbatim. */
  const accent = useActiveAccent();
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('model');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const result = state.run.result;
  if (result === null) {
    const hasData = state.setup !== null;
    return (
      <div className="panel active">
        <div className="card">
          <h2>No Results Yet</h2>
          <p className="sub">
            {hasData
              ? 'Run the model in Step 2. The estimates table, chart, and interpretation appear here.'
              : 'Load a data file in Step 1, then run the model in Step 2. The estimates table, chart, and interpretation appear here.'}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => store.goToStep(hasData ? 'configure' : 'upload')}
          >
            {hasData ? 'Go to Step 2' : 'Go to Step 1'}
          </button>
        </div>
      </div>
    );
  }

  const cellId =
    state.selectedCellId !== null &&
    result.cells.some((c) => c.cellId === state.selectedCellId)
      ? state.selectedCellId
      : (result.cells[0]?.cellId ?? '');
  const cell = result.cells.find((c) => c.cellId === cellId);

  // The state-layer gate: at most ten areas of own data on screen in
  // trial (S3). Everything below renders only what this returns.
  const visible = visibleDomainsForCell(
    result,
    cellId,
    state.license,
    state.source.kind,
  );
  const stats = cellStats(visible.visible);
  const paragraphs = interpretationParagraphs(stats);

  // Readout band readings: the selected cell's statewide figures
  // (proportion-scale model mean and statewide direct, stored on the
  // cell result by the pipeline; the fit diagnostics' aggregation
  // means are transformed-scale and are not display values), its
  // benchmarking outcome (RB-4 status, falling back to the applied
  // flag on results that predate it), and the visible domains'
  // fitted / flagged counts.
  const statewideFigures = cell?.statewideFigures;
  const benchmarkingStatus = cell?.validatedRange?.benchmarkingStatus;
  const benchmarkValue =
    cell === undefined
      ? '—'
      : benchmarkingStatus === 'applied'
        ? 'Applied'
        : benchmarkingStatus === 'user-off'
          ? 'Off'
          : benchmarkingStatus === 'skipped'
            ? 'Skipped'
            : cell.benchmarkApplied === true
              ? 'Applied'
              : '—';
  const benchmarkSub =
    cell?.benchmarkApplied === true && cell.benchmarkingMethod !== undefined
      ? `${cell.benchmarkingMethod} shift`
      : '';

  const filtered = visible.visible.filter((d) =>
    d.domain.areaId.toLowerCase().includes(search.trim().toLowerCase()),
  );
  const sorted = [...filtered].sort((a, b) => {
    const va = sortValue(a, sortKey);
    const vb = sortValue(b, sortKey);
    const cmp =
      typeof va === 'string'
        ? va.localeCompare(String(vb))
        : (va as number) - (vb as number);
    return cmp * sortDir;
  });
  const onSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(key === 'area' ? 1 : -1);
    }
  };
  const sortMark = (key: SortKey) =>
    sortKey === key ? (sortDir === 1 ? ' ↑' : ' ↓') : ' ↕';

  return (
    <div className="panel active">
      {state.run.stale && (
        <p className="stale-note">
          Settings changed since this run. These are the previous results;
          run the model again in Step 2 to update them.
        </p>
      )}
      <div className="toolbar">
        <div className="toolbar-left">
          <input
            className="search"
            type="search"
            placeholder="Search areas…"
            aria-label="Search areas"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {result.cells.length > 1 && (
            <select
              aria-label="Result set"
              value={cellId}
              onChange={(e) => store.selectCell(e.target.value)}
            >
              {result.cells.map((c) => (
                <option key={c.cellId} value={c.cellId}>
                  {c.indicatorId}
                  {c.subgroupVariable !== undefined
                    ? `: ${c.subgroupVariable}: ${String(c.subgroupLevel ?? '')}`
                    : ''}
                </option>
              ))}
            </select>
          )}
          <span className="toggle on">
            Sorted by {sortKey === 'model' ? 'model estimate' : sortKey}
            {sortDir === 1 ? ' ↑' : ' ↓'}
          </span>
        </div>
        <ExportButtons kinds={['area-report-xlsx', 'results-csv']} />
      </div>

      <div className="readout" aria-label="Run summary">
        <div>
          <span className="ro-label">Statewide Estimate</span>
          <span className="ro-value">
            {statewideFigures !== undefined
              ? fmtPct(statewideFigures.modelMean)
              : '—'}
          </span>
          <span className="ro-sub">
            {statewideFigures !== undefined
              ? `Statewide direct ${fmtPct(statewideFigures.directEstimate)}`
              : ''}
          </span>
        </div>
        <div>
          <span className="ro-label">Areas Fitted</span>
          <span className="ro-value">{stats.areasWithModel}</span>
          <span className="ro-sub">of {visible.total} areas</span>
        </div>
        <div>
          <span className="ro-label">Benchmarking</span>
          <span className="ro-value">{benchmarkValue}</span>
          <span className="ro-sub">{benchmarkSub}</span>
        </div>
        <div>
          <span className="ro-label">Flagged Areas</span>
          <span className="ro-value">{stats.flaggedCount}</span>
          <span className="ro-sub">
            {stats.suppressedCount > 0
              ? `${stats.suppressedCount} suppressed`
              : ''}
          </span>
        </div>
      </div>

      {visible.capped && (
        <div className="lock-note">
          <span>🔒</span>
          <span>
            <strong>Trial preview.</strong> Showing ten of {visible.total}{' '}
            areas. A license displays every area and unlocks all exports.{' '}
            <button className="linklike" onClick={openLicenseDialog}>
              Unlock the Tool
            </button>
          </span>
        </div>
      )}

      <div className="stats-row">
        <div className="stat">
          <span>MODEL ESTIMATE RANGE</span>
          <b>
            {stats.modelRange !== null
              ? `${fmtPct(stats.modelRange[0])} – ${fmtPct(stats.modelRange[1])}`
              : '—'}
          </b>
          <small>
            {stats.directRange !== null
              ? `Direct range was ${fmtPct(stats.directRange[0])} – ${fmtPct(stats.directRange[1])}`
              : ''}
          </small>
        </div>
        <div className="stat">
          <span>MEDIAN CV — MODEL</span>
          <b>{fmtCv(stats.medianModelCv ?? undefined)}</b>
          <small>
            {stats.medianDirectCv !== null
              ? `Direct median ${fmtCv(stats.medianDirectCv)}`
              : ''}
          </small>
        </div>
        <div className="stat">
          <span>BIGGEST CORRECTION</span>
          <b>{stats.biggestCorrection?.areaId ?? '—'}</b>
          <small>
            {stats.biggestCorrection !== null &&
            stats.biggestCorrection !== undefined
              ? `${fmtPct(stats.biggestCorrection.direct)} direct → ${fmtPct(stats.biggestCorrection.model)} model`
              : ''}
          </small>
        </div>
        <div className="stat">
          <span>AREAS IMPROVED</span>
          <b>
            {stats.areasImproved} / {stats.areasWithModel}
          </b>
          <small>Model CV below the direct CV</small>
        </div>
      </div>

      {cell?.status === 'insufficient-areas' && (
        <p className="stale-note">
          This set of areas was too small to fit a model, so direct
          estimates are shown. The run report in Step 4 names the reason.
        </p>
      )}

      <div className="table-wrap">
        <table aria-label="Estimates by area">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>
                <button className="th-sort" onClick={() => onSort('area')}>
                  Area{sortMark('area')}
                </button>
              </th>
              <th className="hide-sm">
                <button className="th-sort" onClick={() => onSort('n')}>
                  n{sortMark('n')}
                </button>
              </th>
              <th>
                <button className="th-sort" onClick={() => onSort('direct')}>
                  Direct %{sortMark('direct')}
                </button>
              </th>
              <th className="hide-sm">
                <button className="th-sort" onClick={() => onSort('se')}>
                  Direct SE{sortMark('se')}
                </button>
              </th>
              <th>
                <button className="th-sort" onClick={() => onSort('model')}>
                  Model (EBLUP) %{sortMark('model')}
                </button>
              </th>
              <th>
                <button className="th-sort" onClick={() => onSort('cv')}>
                  Model CV{sortMark('cv')}
                </button>
              </th>
              <th className="hide-sm">
                <button className="th-sort" onClick={() => onSort('gamma')}>
                  Shrinkage γ{sortMark('gamma')}
                </button>
              </th>
              <th>Flag</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => (
              <tr key={d.domain.areaId}>
                <td style={{ textAlign: 'left' }}>
                  <b>{d.domain.areaId}</b>
                </td>
                <td className="hide-sm">{fmtInt(d.reliability.n)}</td>
                <td>
                  {d.direct !== undefined ? fmtPct(d.direct.estimate) : '—'}
                </td>
                <td className="hide-sm">
                  {d.direct !== undefined
                    ? fmtPct(d.direct.standardError)
                    : '—'}
                </td>
                <td>
                  {d.model !== undefined ? (
                    <b>{fmtPct(d.model.estimate)}</b>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{fmtCv(modelCv(d) ?? undefined)}</td>
                <td className="hide-sm">
                  {d.reliability.gamma !== undefined
                    ? d.reliability.gamma.toFixed(2)
                    : '—'}
                </td>
                <td>
                  {flagText(d) !== '' && (
                    <span
                      className={`flag ${d.status}`}
                      title={d.suppressionReason ?? ''}
                    >
                      {flagText(d)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="table-note">
        γ (gamma) is the weight the model places on the direct estimate.
        A low γ means the direct estimate was noisy, and the model
        estimate rests more on the regression prediction. Select any
        column heading to sort. Suppressed values remain visible in this
        workspace; suppression is enforced on exports.
      </p>

      <div className="card chart-card">
        <h2>Direct vs. Model Estimates</h2>
        <p className="sub">
          Gray dots are the direct estimates; {accent.captionWord} dots are the model
          estimates, with the model interval as a bar. Small, noisy areas
          move the most.
        </p>
        <div className="chart-legend">
          <span>
            <i style={{ background: 'var(--chart-direct)' }}></i>Direct estimate
          </span>
          <span>
            <i style={{ background: 'var(--orange)' }}></i>Model (EBLUP)
          </span>
          <span>
            <i style={{ background: 'var(--orange)' }}></i>Model interval
          </span>
        </div>
        <ShrinkageChart domains={visible.visible} />
      </div>

      <div className="interp">
        <h2>Reading These Results</h2>
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </div>
  );
}
